import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDeleteTask, useTask, useTaskDeviceRefresh, usePatchTask } from "../../api/tasks";
import { StatusBadge } from "../../components/StatusBadge";
import { TaskDeviceProgressTable } from "../../components/TaskDeviceProgressTable";
import { extractErrorMessage } from "../../api/errors";
import { Pencil, Trash2 } from "lucide-react";

export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useTask(id ?? "");
  // Polls device progress while the rollout runs and refetches once more
  // when it stops, so the final device states always land.
  const deviceRefreshInterval = useTaskDeviceRefresh(id ?? "", data?.data.status);
  const patchTask = usePatchTask();
  const deleteTask = useDeleteTask();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [formError, setFormError] = useState("");

  if (isLoading) return <p className="text-sm text-gray-400 dark:text-riot-navy-400">Loading task...</p>;
  if (error) return <p className="text-sm text-red-600 dark:text-red-400">Error: {extractErrorMessage(error, "Failed to load task")}</p>;
  if (!data) return null;

  const t = data.data;
  const total = t.totalDevices || 1;
  const completedPct = Math.round((t.completedDevices / total) * 100);
  const failedPct = Math.round((t.failedDevices / total) * 100);

  const canPause = t.status === "in_progress";
  const canResume = t.status === "paused";
  const canCancel = ["scheduled", "queued", "in_progress", "paused"].includes(t.status);
  const targetIds = t.targetIds ?? (t.targetId ? [t.targetId] : []);
  const targetLabel = t.targetType === "all"
    ? "All devices"
    : `${t.targetType}: ${targetIds.join(", ")}`;

  function openEdit() {
    setEditName(t.name);
    setEditDescription(t.description ?? "");
    setFormError("");
    setEditOpen(true);
  }

  async function saveEdit() {
    setFormError("");
    try {
      await patchTask.mutateAsync({ id: t.taskId, name: editName.trim(), description: editDescription });
      setEditOpen(false);
    } catch (err) {
      setFormError(extractErrorMessage(err, "Failed to update task"));
    }
  }

  async function confirmDelete() {
    setFormError("");
    try {
      await deleteTask.mutateAsync(t.taskId);
      navigate("/tasks");
    } catch (err) {
      setFormError(extractErrorMessage(err, "Failed to delete task"));
    }
  }

  return (
    <div>
      <button
        onClick={() => navigate("/tasks")}
        className="mb-4 text-xs text-riot-gold-600 hover:underline dark:text-riot-gold-400"
      >
        &larr; Back to tasks
      </button>

      <div className="mb-6 rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t.name}</h2>
          <div className="flex items-center gap-2">
            <StatusBadge status={t.status} />
            <button
              type="button"
              onClick={openEdit}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"
            >
              <Pencil size={12} /> Edit
            </button>
            {canPause && (
              <button
                onClick={() => patchTask.mutate({ id: t.taskId, action: "pause" })}
                className="rounded-md border border-orange-300 px-3 py-1 text-xs font-medium text-orange-700 hover:bg-orange-50 dark:border-orange-800/60 dark:text-orange-300 dark:hover:bg-orange-950/40"
              >
                Pause
              </button>
            )}
            {canResume && (
              <button
                onClick={() => patchTask.mutate({ id: t.taskId, action: "resume" })}
                className="rounded-md border border-green-300 px-3 py-1 text-xs font-medium text-green-700 hover:bg-green-50 dark:border-green-800/60 dark:text-green-300 dark:hover:bg-green-950/40"
              >
                Resume
              </button>
            )}
            {canCancel && (
              <button
                onClick={() => patchTask.mutate({ id: t.taskId, action: "cancel" })}
                className="rounded-md border border-red-300 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:border-red-800/60 dark:text-red-300 dark:hover:bg-red-950/40"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setFormError("");
                setDeleteOpen(true);
              }}
              className="inline-flex items-center gap-1 rounded-md border border-red-300 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:border-red-800/60 dark:text-red-300 dark:hover:bg-red-950/40"
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="mb-1 flex justify-between text-xs text-gray-500 dark:text-riot-navy-300">
            <span>{t.completedDevices} completed, {t.failedDevices} failed of {t.totalDevices}</span>
            <span>{completedPct + failedPct}%</span>
          </div>
          <div className="flex h-2.5 overflow-hidden rounded-full bg-gray-200 dark:bg-riot-navy-700">
            {/* Widths are runtime percentages; setProperty via ref
                callback keeps the DOM free of inline style="..." so
                CSP `style-src 'self'` holds. */}
            <div
              ref={(el) => {
                if (el) el.style.setProperty("--w", `${completedPct}%`);
              }}
              className="bg-green-500 [width:var(--w)]"
            />
            <div
              ref={(el) => {
                if (el) el.style.setProperty("--w", `${failedPct}%`);
              }}
              className="bg-red-500 [width:var(--w)]"
            />
          </div>
        </div>

        <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs text-gray-500 dark:text-riot-navy-400">
              {t.taskType === "resource_update" ? "Resource Bundle" : "Firmware"}
            </dt>
            <dd className="text-gray-900 dark:text-riot-navy-100">
              {t.taskType === "resource_update"
                ? `${t.resourceBundleId}${t.resourceBundleVersion ? ` v${t.resourceBundleVersion}` : ""}`
                : t.firmwareId}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 dark:text-riot-navy-400">Strategy</dt>
            <dd className="text-gray-900 dark:text-riot-navy-100">{t.rolloutStrategy}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 dark:text-riot-navy-400">Target</dt>
            <dd className="text-gray-900 dark:text-riot-navy-100">{targetLabel}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 dark:text-riot-navy-400">Created By</dt>
            <dd className="text-gray-900 dark:text-riot-navy-100">{t.createdBy}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 dark:text-riot-navy-400">Created</dt>
            <dd className="text-gray-900 dark:text-riot-navy-100">{new Date(t.createdAt).toLocaleString()}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 dark:text-riot-navy-400">Started</dt>
            <dd className="text-gray-900 dark:text-riot-navy-100">{t.startedAt ? new Date(t.startedAt).toLocaleString() : "-"}</dd>
          </div>
        </dl>
      </div>

      <div>
        <TaskDeviceProgressTable
          taskId={t.taskId}
          title="Per-Device Progress"
          totalDevices={t.totalDevices}
          refetchInterval={deviceRefreshInterval}
        />
      </div>

      {editOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true" aria-label="Edit task">
          <div className="w-full max-w-lg rounded-lg border border-riot-navy-700 bg-white p-5 shadow-xl dark:bg-riot-navy-900">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Edit task</h3>
            <div className="mt-4 space-y-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-riot-navy-100">
                Task name
                <input
                  value={editName}
                  onChange={(event) => setEditName(event.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
                />
              </label>
              <label className="block text-sm font-medium text-gray-700 dark:text-riot-navy-100">
                Description
                <textarea
                  value={editDescription}
                  onChange={(event) => setEditDescription(event.target.value)}
                  rows={4}
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
                />
              </label>
            </div>

            {formError && <p className="mt-3 text-sm text-red-600 dark:text-red-300">{formError}</p>}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditOpen(false)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={patchTask.isPending || !editName.trim()}
                className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50"
              >
                {patchTask.isPending ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true" aria-label="Delete task confirmation">
          <div className="w-full max-w-md rounded-lg border border-riot-navy-700 bg-white p-5 shadow-xl dark:bg-riot-navy-900">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Delete task?</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-riot-navy-200">
              {t.name} will be cancelled if it is still active, then removed from task lists.
            </p>

            {formError && <p className="mt-3 text-sm text-red-600 dark:text-red-300">{formError}</p>}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteOpen(false)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleteTask.isPending}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 active:bg-red-800 disabled:opacity-50"
              >
                {deleteTask.isPending ? "Deleting..." : "Delete task"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
